import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Loader2, RefreshCw, Package, Users, ShoppingBag, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { StatsBar } from "@/components/dashboard/StatsBar";
import { OrderFilters } from "@/components/dashboard/OrderFilters";
import { OrdersTable } from "@/components/dashboard/OrdersTable";
import { BulkActionsBar } from "@/components/dashboard/BulkActionsBar";
import { OrderDetailModal, Order, formatUKDateTime } from "@/components/dashboard/OrderDetailModal";
import { CustomersPage } from "@/components/dashboard/CustomersPage";
import { ProductsPage } from "@/components/dashboard/ProductsPage";
import { SettingsPage } from "@/components/dashboard/SettingsPage";

const OwnerDashboard = () => {
  const { user, session } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read tab from URL, default to 'orders'
  const urlTab = searchParams.get('tab') || 'orders';
  const [activeTab, setActiveTab] = useState(urlTab);
  
  // Sync tab with URL
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'orders';
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);
  
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    if (newTab === 'orders') {
      setSearchParams({});
    } else {
      setSearchParams({ tab: newTab });
    }
  };

  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [fulfilmentFilter, setFulfilmentFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");

  // Order detail modal
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Bulk selection
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());

  // Fetch orders when authenticated
  useEffect(() => {
    if (user && session) {
      fetchOrders();
    }
  }, [user, session]);

  const fetchOrders = async () => {
    if (!session?.access_token) {
      setOrdersError("No valid session. Please log in again.");
      return;
    }

    setIsLoadingOrders(true);
    setOrdersError("");

    try {
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: { action: "fetch" },
      });

      if (error) throw error;

      if (data.error) {
        setOrdersError(data.error);
        return;
      }

      setOrders(data.orders || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setOrdersError("Failed to load orders. Please try again.");
    } finally {
      setIsLoadingOrders(false);
      setInitialLoad(false);
    }
  };

  const handleUpdateOrder = async (orderId: string, updates: Partial<Order>) => {
    if (!session?.access_token) {
      throw new Error("No valid session");
    }

    const { data, error } = await supabase.functions.invoke("admin-orders", {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body: { action: "update", orderId, updates },
    });

    if (error || data.error) {
      throw new Error(data?.error || "Update failed");
    }

    // Update local state
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId ? { ...order, ...updates, ...data.order } : order
      )
    );

    // Update selected order if it's the one being viewed
    if (selectedOrder?.id === orderId) {
      setSelectedOrder((prev) => (prev ? { ...prev, ...updates, ...data.order } : null));
    }
  };

  const handleToggleFulfilled = async (order: Order) => {
    try {
      await handleUpdateOrder(order.id, { fulfilled: !order.fulfilled });
      toast.success(order.fulfilled ? "Marked unfulfilled" : "Marked fulfilled");
    } catch {
      toast.error("Failed to update order");
    }
  };

  const handleBulkUpdate = async (orderIds: string[], updates: Partial<Order>) => {
    if (!session?.access_token) {
      throw new Error("No valid session");
    }

    const { data, error } = await supabase.functions.invoke("admin-orders", {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body: { action: "bulk_update", orderIds, updates },
    });

    if (error || data.error) {
      throw new Error(data?.error || "Bulk update failed");
    }

    // Update local state
    setOrders((prev) =>
      prev.map((order) =>
        orderIds.includes(order.id) ? { ...order, ...updates } : order
      )
    );

    // Clear selection after bulk action
    setSelectedOrderIds(new Set());
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  // Helper to calculate order age
  const getOrderAge = useCallback((dateStr: string) => {
    const created = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }, []);

  // Pipeline counts
  const pipelineCounts = useMemo(() => {
    const readyToShip = orders.filter(
      (o) => !o.fulfilled && o.payment_status === "paid"
    ).length;
    const needsTracking = orders.filter(
      (o) => o.fulfilled && !o.tracking_number
    ).length;
    const overdue = orders.filter(
      (o) => !o.fulfilled && getOrderAge(o.created_at) >= 3
    ).length;

    return { readyToShip, needsTracking, overdue };
  }, [orders, getOrderAge]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    let result = orders;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (order) =>
          order.customer_email.toLowerCase().includes(query) ||
          order.order_number.toLowerCase().includes(query) ||
          order.customer_name.toLowerCase().includes(query)
      );
    }

    // Fulfilment filter
    if (fulfilmentFilter === "fulfilled") {
      result = result.filter((order) => order.fulfilled);
    } else if (fulfilmentFilter === "unfulfilled") {
      result = result.filter((order) => !order.fulfilled);
    } else if (fulfilmentFilter === "ready_to_ship") {
      result = result.filter(
        (order) => !order.fulfilled && order.payment_status === "paid"
      );
    } else if (fulfilmentFilter === "needs_tracking") {
      result = result.filter(
        (order) => order.fulfilled && !order.tracking_number
      );
    } else if (fulfilmentFilter === "overdue") {
      result = result.filter(
        (order) => !order.fulfilled && getOrderAge(order.created_at) >= 3
      );
    }

    // Date range filter
    const now = new Date();
    if (dateRange === "today") {
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      result = result.filter((order) => new Date(order.created_at) >= startOfToday);
    } else if (dateRange === "7days") {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      result = result.filter((order) => new Date(order.created_at) >= sevenDaysAgo);
    } else if (dateRange === "30days") {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      result = result.filter((order) => new Date(order.created_at) >= thirtyDaysAgo);
    } else if (dateRange === "90days") {
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      result = result.filter((order) => new Date(order.created_at) >= ninetyDaysAgo);
    }

    return result;
  }, [orders, searchQuery, fulfilmentFilter, dateRange, getOrderAge]);

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const unfulfilledOrders = orders.filter((o) => !o.fulfilled).length;
    const revenueLast7Days = orders
      .filter((o) => new Date(o.created_at) >= sevenDaysAgo)
      .reduce((sum, o) => sum + o.total, 0);
    const revenueToday = orders
      .filter((o) => new Date(o.created_at) >= startOfToday)
      .reduce((sum, o) => sum + o.total, 0);

    return {
      totalOrders: orders.length,
      unfulfilledOrders,
      revenueLast7Days,
      revenueToday,
    };
  }, [orders]);

  const hasActiveFilters =
    searchQuery !== "" || fulfilmentFilter !== "all" || dateRange !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setFulfilmentFilter("all");
    setDateRange("all");
  };

  // Selection handlers
  const handleToggleSelect = (orderId: string) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedOrderIds(new Set(filteredOrders.map((o) => o.id)));
  };

  const handleDeselectAll = () => {
    setSelectedOrderIds(new Set());
  };

  // Get selected orders for bulk actions
  const selectedOrders = useMemo(() => {
    return orders.filter((o) => selectedOrderIds.has(o.id));
  }, [orders, selectedOrderIds]);

  // Export functions
  const exportCSV = (ordersToExport: Order[] = filteredOrders) => {
    const headers = [
      "Order ID",
      "Date",
      "Customer Name",
      "Customer Email",
      "Phone",
      "Address",
      "Postcode",
      "Total",
      "Payment Status",
      "Fulfilment Status",
      "Fulfilled At",
      "Items Count",
      "Tracking Number",
      "Courier",
      "Notes",
    ];

    const rows = ordersToExport.map((order) => [
      order.order_number,
      formatUKDateTime(order.created_at),
      order.customer_name,
      order.customer_email,
      order.customer_phone || "",
      [
        order.delivery_address.line1,
        order.delivery_address.line2,
        order.delivery_address.city,
        order.delivery_address.county,
      ]
        .filter(Boolean)
        .join(", "),
      order.delivery_address.postcode || "",
      `£${order.total.toFixed(2)}`,
      order.payment_status,
      order.fulfilled ? "Fulfilled" : "Unfulfilled",
      order.fulfilled_at ? formatUKDateTime(order.fulfilled_at) : "",
      order.items.length.toString(),
      order.tracking_number || "",
      order.courier || "",
      order.internal_notes || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `orders-export-${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${ordersToExport.length} orders`);
  };

  const printPackingSlip = (order: Order) => {
    printPackingSlips([order]);
  };

  const printPackingSlips = (ordersToPrint: Order[]) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow popups to print packing slips");
      return;
    }

    const formatAddress = (addr: Order["delivery_address"]) => {
      return [addr.line1, addr.line2, addr.city, addr.county, addr.postcode, addr.country]
        .filter(Boolean)
        .join("<br>");
    };

    const slipsHtml = ordersToPrint
      .map(
        (order) => `
      <div class="slip">
        <div class="header">
          <div class="logo">SAMBOARDS</div>
          <h1>Packing Slip</h1>
        </div>
        
        <div class="order-info">
          <div class="info-row">
            <span class="label">Order ID:</span>
            <span class="value">${order.order_number}</span>
          </div>
          <div class="info-row">
            <span class="label">Date/Time:</span>
            <span class="value">${formatUKDateTime(order.created_at)}</span>
          </div>
        </div>
        
        <div class="section">
          <h2>Customer Details</h2>
          <div class="customer-name">${order.customer_name}</div>
          <div class="customer-contact">${order.customer_email}</div>
          ${order.customer_phone ? `<div class="customer-contact">${order.customer_phone}</div>` : ""}
        </div>
        
        <div class="section">
          <h2>Delivery Address</h2>
          <div class="address">${formatAddress(order.delivery_address)}</div>
        </div>
        
        <div class="section">
          <h2>Order Items</h2>
          <table>
            <thead>
              <tr>
                <th class="col-product">Product</th>
                <th class="col-qty">Qty</th>
                <th class="col-price">Unit Price</th>
                <th class="col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              ${order.items
                .map(
                  (item) => `
                <tr>
                  <td class="col-product">${item.name}</td>
                  <td class="col-qty">${item.quantity}</td>
                  <td class="col-price">£${item.price.toFixed(2)}</td>
                  <td class="col-total">£${(item.price * item.quantity).toFixed(2)}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          <div class="totals">
            <div class="total-row">
              <span>Subtotal</span>
              <span>£${order.subtotal.toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Shipping</span>
              <span>£${order.shipping.toFixed(2)}</span>
            </div>
            <div class="total-row grand-total">
              <span>Total</span>
              <span>£${order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
        
        ${order.internal_notes ? `
        <div class="section notes-section">
          <h2>Internal Notes</h2>
          <div class="notes">${order.internal_notes.replace(/\n/g, '<br>')}</div>
        </div>
        ` : ""}
        
        <div class="footer">
          <p>Thank you for your order!</p>
          <p class="small">Samboards Ltd • www.samboards.co.uk</p>
        </div>
      </div>
    `
      )
      .join('<div class="page-break"></div>');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Packing Slips - Samboards</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; 
              padding: 20px;
              color: #000;
              background: #fff;
              font-size: 12px;
              line-height: 1.5;
            }
            .slip { 
              max-width: 600px; 
              margin: 0 auto;
              padding: 30px;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 20px;
              margin-bottom: 20px;
            }
            .logo {
              font-size: 24px;
              font-weight: 700;
              letter-spacing: 3px;
              margin-bottom: 5px;
            }
            h1 { 
              font-size: 16px; 
              font-weight: 400;
              text-transform: uppercase;
              letter-spacing: 2px;
            }
            .order-info {
              display: flex;
              justify-content: space-between;
              background: #f5f5f5;
              padding: 12px 16px;
              margin-bottom: 20px;
              border: 1px solid #ddd;
            }
            .info-row {
              display: flex;
              gap: 8px;
            }
            .info-row .label {
              font-weight: 600;
            }
            .info-row .value {
              font-family: 'Courier New', monospace;
            }
            .section {
              margin-bottom: 20px;
            }
            h2 { 
              font-size: 11px; 
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              border-bottom: 1px solid #ddd;
              padding-bottom: 6px;
              margin-bottom: 10px;
            }
            .customer-name {
              font-size: 14px;
              font-weight: 600;
              margin-bottom: 4px;
            }
            .customer-contact {
              color: #444;
            }
            .address { 
              line-height: 1.8; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
            }
            th, td { 
              text-align: left; 
              padding: 10px 8px; 
              border-bottom: 1px solid #ddd; 
            }
            th { 
              background: #f9f9f9; 
              font-weight: 600;
              text-transform: uppercase;
              font-size: 10px;
              letter-spacing: 0.5px;
            }
            .col-qty, .col-price, .col-total { text-align: right; width: 80px; }
            .col-product { width: auto; }
            td.col-qty, td.col-price, td.col-total { 
              text-align: right; 
              font-family: 'Courier New', monospace;
            }
            .totals {
              margin-top: 15px;
              border-top: 2px solid #000;
              padding-top: 10px;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              padding: 6px 0;
              font-family: 'Courier New', monospace;
            }
            .total-row.grand-total {
              font-weight: 700;
              font-size: 14px;
              border-top: 1px solid #ddd;
              margin-top: 8px;
              padding-top: 10px;
            }
            .notes-section {
              background: #fffef0;
              border: 1px dashed #ccc;
              padding: 12px;
              margin-top: 20px;
            }
            .notes {
              font-style: italic;
              color: #555;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
            }
            .footer p {
              margin-bottom: 4px;
            }
            .footer .small {
              font-size: 10px;
              color: #888;
            }
            .page-break { 
              page-break-after: always; 
              height: 0; 
            }
            @media print { 
              body { padding: 0; } 
              .slip { padding: 0; }
              .page-break { display: block; }
              .notes-section { background: #fff; }
            }
          </style>
        </head>
        <body>
          ${slipsHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Show loading on initial load
  if (initialLoad && isLoadingOrders) {
    return (
      <>
        <Helmet>
          <title>Order Fulfillment Centre - Samboards</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
            <p className="text-slate-400 text-sm">Loading dashboard...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Order Fulfillment Centre - Samboards</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-100">
              Owner Portal
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Manage orders, customers, and products
            </p>
          </div>
          <Button
            onClick={fetchOrders}
            disabled={isLoadingOrders}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            {isLoadingOrders ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </>
            )}
          </Button>
        </div>

        {/* Stats Bar */}
        <StatsBar
          totalOrders={stats.totalOrders}
          unfulfilledOrders={stats.unfulfilledOrders}
          revenueLast7Days={stats.revenueLast7Days}
          revenueToday={stats.revenueToday}
        />

        {/* Error Alert */}
        {ordersError && (
          <Alert
            variant="destructive"
            className="bg-red-900/20 border-red-900/50"
          >
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{ordersError}</AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
            <TabsTrigger
              value="orders"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300"
            >
              <Package className="h-4 w-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger
              value="customers"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300"
            >
              <Users className="h-4 w-4 mr-2" />
              Customers
            </TabsTrigger>
            <TabsTrigger
              value="products"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300"
            >
              <ShoppingBag className="h-4 w-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300"
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" className="mt-6">
            {/* Filters */}
            <OrderFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              fulfilmentFilter={fulfilmentFilter}
              onFulfilmentChange={setFulfilmentFilter}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              onExportCSV={() => exportCSV()}
              onClearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
              pipelineCounts={pipelineCounts}
            />

            {/* Loading state for refresh */}
            {isLoadingOrders && !initialLoad ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
                <span className="ml-3 text-slate-400">Loading orders...</span>
              </div>
            ) : (
              <>
                {/* Results count */}
                {hasActiveFilters && (
                  <p className="text-slate-400 text-sm mb-4">
                    Showing {filteredOrders.length} of {orders.length} orders
                  </p>
                )}

                {/* Orders Table */}
                <OrdersTable
                  orders={filteredOrders}
                  onViewOrder={handleViewOrder}
                  selectedOrderIds={selectedOrderIds}
                  onToggleSelect={handleToggleSelect}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={handleDeselectAll}
                  onToggleFulfilled={handleToggleFulfilled}
                  onPrintPackingSlip={printPackingSlip}
                />
              </>
            )}
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="mt-6">
            <CustomersPage orders={orders} onViewOrder={handleViewOrder} />
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="mt-6">
            <ProductsPage orders={orders} onViewOrder={handleViewOrder} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <SettingsPage />
          </TabsContent>
        </Tabs>
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedOrderIds.size}
        selectedOrders={selectedOrders}
        onMarkFulfilled={() =>
          handleBulkUpdate(Array.from(selectedOrderIds), { fulfilled: true })
        }
        onMarkUnfulfilled={() =>
          handleBulkUpdate(Array.from(selectedOrderIds), { fulfilled: false })
        }
        onExportSelected={() => exportCSV(selectedOrders)}
        onPrintPackingSlips={() => printPackingSlips(selectedOrders)}
        onClearSelection={handleDeselectAll}
      />

      {/* Order Detail Modal */}
      <OrderDetailModal
        order={selectedOrder}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onUpdate={handleUpdateOrder}
      />
    </>
  );
};

export default OwnerDashboard;